// Calendar-day arithmetic for the planning components and screens. A plan works in whole
// LOCAL days, keyed as yyyy-MM-dd - never through Date's UTC parsing, which shifts a date by a
// day either side of midnight depending on the time zone.

/** yyyy-MM-dd for a local date. */
export function toDayKey(date: Date): string {
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A local date from yyyy-MM-dd (anything after the first ten characters is ignored). */
export function fromDayKey(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function addCalendarDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** The Monday on or before a date. */
export function mondayOfWeek(date: Date): Date {
  return addCalendarDays(date, -((date.getDay() + 6) % 7));
}

/** Whole days from one date to another. */
export function daysBetween(from: Date, to: Date): number {
  return Math.round((fromDayKey(toDayKey(to)).getTime() - fromDayKey(toDayKey(from)).getTime()) / 86400000);
}

/**
 * The days of the week as .NET's DayOfWeek names, in its order (Sunday = 0) - what the API sends
 * and expects. The screen shows the LOCAL name for each (weekdayLabel), never these.
 */
export const DayOfWeekNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The local name of a day of the week (0 = Sunday), via Angular's own date formatting so it follows
 * the user's language - 'EEE' short ("Mon"), 'EEEE' long ("Monday").
 */
export function weekdayLabel(dayOfWeek: number, format: (date: Date, pattern: string) => string, long = false) {
  // 1 November 2026 is a Sunday, so day N of the week is N days after it.
  return format(new Date(2026, 10, 1 + dayOfWeek), long ? 'EEEE' : 'EEE');
}

/** Times of day every `stepMinutes`, as "HH:mm" - 00:00 up to the last step before midnight. */
export function timesOfDay(stepMinutes = 15): string[] {
  const pad = (value: number) => value.toString().padStart(2, '0');
  const times: string[] = [];

  for (let minutes = 0; minutes < 24 * 60; minutes += stepMinutes) {
    times.push(`${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
  }

  return times;
}

/** ISO 8601 week number: week 1 is the week holding the year's first Thursday. */
export function isoWeek(date: Date): number {
  const thursday = addCalendarDays(date, 3 - ((date.getDay() + 6) % 7));
  const january4 = new Date(thursday.getFullYear(), 0, 4);
  const firstThursday = addCalendarDays(january4, 3 - ((january4.getDay() + 6) % 7));
  return 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 86400000));
}
